'use client';

import { useParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';

import { doTranslation } from '@/app/i18n/client';
import ButtonTertiary from '@/components/global/buttons/button-tertiary';
import { localeSpecificPath } from '@/utils/localeUtils';

import styles from '@/components/loaders/loader.module.scss';

import loaderGif from '@/public/assets/images/global/loaders/loader.gif';

type Props = {
  isInitializedFrom: string;
};

const Loader = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();

  const { t } = doTranslation(lng);

  const [isGIFLoaded, setIsGIFLoaded] = useState(false);
  const [showDelayedComponent, setShowDelayedComponent] = useState(false);
  const [visible, setVisible] = useState<boolean>(true);

  useEffect(() => {
    const image = new window.Image();
    image.src = loaderGif.src;
    image.onload = () => {
      setIsGIFLoaded(true);
    };
    if (props.isInitializedFrom !== '/') {
      const timer = setTimeout(() => {
        setShowDelayedComponent(true);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setShowDelayedComponent(true);
    }
    focusTitle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setVisible(false);
      setTimeout(() => {
        focusTitle();
        setVisible(true);
      });
    }, 3000);
    return () => {
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const focusTitle = () => {
    const titleElement = document.querySelector(`.${styles.one}`);
    (titleElement as HTMLDivElement)?.focus();
  };

  return (
    <div
      aria-atomic="true"
      aria-labelledby={`loading-labelledby`}
      aria-live="polite"
      className={styles.one}
      role="alert"
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      {showDelayedComponent && (
        <>
          <div className={styles.overlay}>
            <div className={styles.loading}>
              {props.isInitializedFrom !== '' && (
                <>
                  <div aria-hidden="true" className={styles.title}>
                    {t("We're searching for our best results...")}
                  </div>
                  <div className="sr-only">{t("We're searching for our best results")}</div>
                </>
              )}
              {<div aria-hidden="true" id="loading-labelledby" className={isGIFLoaded ? styles.gif : styles.svg}></div>}
              {props.isInitializedFrom === '' && visible ? <div className="sr-only">{t('Loading')}</div> : null}
              {props.isInitializedFrom !== '' && (
                <div className={styles.button}>
                  <ButtonTertiary
                    light
                    onClick={(event: any) => {
                      event.preventDefault();
                      const isInitializedFrom = localeSpecificPath(props.isInitializedFrom, lng);
                      window.location.href = `${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}${isInitializedFrom}`;
                    }}
                  >
                    {t('Stop Searching')}
                  </ButtonTertiary>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Loader;
